import React, { useEffect } from 'react'
import { useSignal } from '@preact/signals-react'

import { AppButtonIcon, AppModal } from '@components'
import { useToast } from '@hooks'


/**
 * Renders a chart inline with a fullscreen toggle, and again inside a fullscreen
 * modal while open.
 *
 * `renderChart` receives the props that differ between the two — id, height,
 * title and actions — so the series and axis config live in one place instead of
 * being duplicated per view, which is how the two copies drift apart. In the
 * modal the title goes to the modal header (`title`, `subtitle`, `icon`), as the
 * analyzers' price chart does, and the chart is drawn without its own.
 */
export default function ChartFullScreen({ rootID, height = 500, renderChart, title, subtitle, icon }) {
	const isOpen = useSignal(false)
	const { showToast } = useToast()

	const toggle = () => {
		isOpen.value = !isOpen.value
	}

	useEffect(() => {
		if (isOpen.value) showToast.info("Press ESC to exit fullscreen mode")
	}, [isOpen.value])

	return <>
		{renderChart({
			rootID,
			sx: { height },
			cardProps: {
				actions: <AppButtonIcon
					sx={{ mr: 2 }}
					iconProps={{ iconWeight: "solid" }}
					tooltip="Fullscreen"
					icon="expand-wide"
					onClick={toggle}
				/>
			},
		})}

		<AppModal
			isOpen={isOpen.value}
			controller={toggle}
			title={title}
			subtitle={subtitle}
			icon={icon}
			containerProps={{ fullScreen: true, sx: {
				'.MuiDialogContent-root': { px: 3, pt: 0, pb: 2 },
				'.MuiDialogContent-root .MuiCard-root': { border: 'none', boxShadow: 'none', borderRadius: 0 },
			}}}
			hideActions>
			{renderChart({
				// A second amcharts root cannot share the inline chart's id.
				rootID: `${rootID}Fullscreen`,
				sx: { height: 'calc(100vh - 190px)' },
				title: undefined,
			})}
		</AppModal>
	</>
}
